/** SUNTRONIX ONE — ONE GROUP: Establishments admin (Administration → Organisation). */
import { EntityLogoField } from "@/components/brand/EntityLogoField";
import { useCallback, useEffect, useState } from "react";
import { Building2, Plus, Loader2, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/app/vibe/StatusPill";

const FIELDS = [
  ["legal_name", "Legal name"], ["display_name", "Display name"], ["short_name", "Short name"],
  ["entity_type", "Entity type"], ["pan", "PAN"], ["gstin", "GSTIN"], ["cin", "CIN / Reg. no"],
  ["registered_address", "Registered address"], ["billing_address", "Billing address"],
  ["currency", "Currency"], ["timezone", "Time zone"], ["numbering_prefix", "Numbering prefix"],
  ["website", "Website"], ["email", "Primary email"], ["phone", "Phone"],
];

export default function Establishments() {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => api.get("/establishments").then(({ data }) => setList(data.items)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {}; FIELDS.forEach(([k]) => { patch[k] = active[k] ?? ""; });
      if (active.status === "TO_BE_CONFIGURED" && active.legal_name) patch.status = "ACTIVE";
      const { data } = await api.patch(`/establishments/${active.id}`, patch);
      toast.success("Establishment saved"); setActive(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const create = async () => {
    setCreating(true);
    try { const { data } = await api.post("/establishments", { display_name: `Establishment ${(list?.length || 0) + 1}` }); toast.success("Establishment added — complete its setup"); load(); setActive(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Only a Super Admin can add establishments"); }
    finally { setCreating(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="establishments-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><Building2 className="h-5 w-5" /></div>
          <div><h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Establishments</h1><p className="text-[13px] text-slate-500">ONE GROUP — legal entities inside your operating group. Masters stay shared; each entity keeps its own legal, tax and branding context.</p></div>
        </div>
        <Button onClick={create} disabled={creating} data-testid="establishment-add-btn" className="bg-slate-900 hover:bg-slate-800">{creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add establishment</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          {!list ? <div className="text-sm text-slate-400 py-6">Loading…</div> : list.map((e) => (
            <button key={e.id} onClick={() => setActive(e)} data-testid={`establishment-${e.id}`}
              className={"w-full text-left rounded-xl border p-3 transition-colors " + (active?.id === e.id ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300")}>
              <div className="flex items-center justify-between"><span className="text-[13px] font-semibold text-slate-800">{e.display_name}</span><StatusPill status={e.status === "TO_BE_CONFIGURED" ? "PENDING" : e.status} label={e.status === "TO_BE_CONFIGURED" ? "Setup" : e.status} /></div>
              <div className="text-[11px] text-slate-400 mt-0.5">{e.legal_name || "Legal details to be configured"}{e.gstin ? ` · ${e.gstin}` : ""}</div>
            </button>
          ))}
        </div>
        <div className="lg:col-span-2">
          {!active ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="establishment-empty">Select an establishment to view or edit its legal identity.</div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="establishment-editor">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">{active.display_name}{active.is_primary && <span className="ml-2 text-[10px] text-emerald-600 uppercase tracking-wide">Primary</span>}</div>
                <Button onClick={save} disabled={saving} size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="establishment-save-btn">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FIELDS.map(([k, label]) => (
                  <div key={k}><div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
                    <Input value={active[k] ?? ""} onChange={(e) => setActive({ ...active, [k]: e.target.value })} data-testid={`establishment-field-${k}`} /></div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">Issuer-owned documents use this establishment's own identity: logo and approved letterhead, bank accounts (Admin → Bank Details), GST identity and document branding. Customer and product masters stay shared across ONE GROUP. Tax invoices are uploaded and numbered in the existing invoice workflow (no generator).</p>
              <EntityLogoField est={active} onSaved={(lg) => setActive((a) => ({ ...a, branding: { ...(a.branding || {}), logo: lg } }))} />
              <UserAccess establishment={active} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function UserAccess({ establishment }) {
  const eid = establishment.id;
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users", { params: { q: q || undefined, limit: 25 } }); setUsers(data.items || []); }
    catch { setUsers([]); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const hasAccess = (u) => (u.establishment_ids || []).includes(eid) || u.role === "super_admin" || u.role === "management";
  const roleFor = (u) => (u.establishment_roles || {})[eid] || (u.role === "super_admin" || u.role === "management" ? "all" : "—");

  const setAccess = async (u, grant, role) => {
    try {
      await api.post(`/establishments/${eid}/access`, { user_id: u.id, email: u.email, grant, role });
      toast.success(grant ? "Access granted" : "Access revoked"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Only a Super Admin can change access"); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid="establishment-user-access">
      <div className="text-sm font-semibold text-slate-900 mb-1">User access to {establishment.display_name}</div>
      <p className="text-[11px] text-slate-400 mb-2">Grant a user access and set their role for this establishment. Management &amp; Super Admin have access to all establishments.</p>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name or email…" className="max-w-sm h-9 mb-2" data-testid="access-user-search" />
      {loading ? <div className="text-[12px] text-slate-400 py-3">Loading…</div> : (
        <div className="space-y-1.5 max-h-64 overflow-auto">
          {users.map((u) => {
            const managed = u.role === "super_admin" || u.role === "management";
            const on = hasAccess(u);
            return (
              <div key={u.id || u.email} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`access-user-${u.id || u.email}`}>
                <div><div className="text-[13px] text-slate-800">{u.name || u.email}</div><div className="text-[11px] text-slate-400">{u.email} · {u.role}{on ? ` · role: ${roleFor(u)}` : ""}</div></div>
                {managed ? <span className="text-[11px] text-emerald-600">All establishments</span> : (
                  <div className="flex items-center gap-2">
                    <select defaultValue={roleFor(u) === "—" ? "viewer" : roleFor(u)} onChange={(e) => on && setAccess(u, true, e.target.value)} className="h-8 rounded border border-slate-200 text-[12px] px-1" data-testid={`access-role-${u.id || u.email}`} disabled={!on}>
                      {["manager", "viewer"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {on
                      ? <Button size="sm" variant="outline" onClick={() => setAccess(u, false)} data-testid={`access-revoke-${u.id || u.email}`}>Revoke</Button>
                      : <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => setAccess(u, true, "viewer")} data-testid={`access-grant-${u.id || u.email}`}>Grant</Button>}
                  </div>
                )}
              </div>
            );
          })}
          {users.length === 0 && <div className="text-[12px] text-slate-400 py-3">No users found.</div>}
        </div>
      )}
    </div>
  );
}

